import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

interface LoginModalProps {
  onClose: () => void;
}

export default function LoginModal({ onClose }: LoginModalProps) {
  const { login } = useAuth();
  const [memberId, setMemberId] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [msg, setMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!memberId.trim()) { setMsg('아이디를 입력해 주세요.'); setStatus('error'); return; }
    if (!password.trim()) { setMsg('전화번호(비밀번호)를 입력해 주세요.'); setStatus('error'); return; }

    setStatus('loading');
    setMsg('');
    const result = await login(memberId, password);
    if (result.success) {
      setStatus('success');
      setMsg(result.msg);
      setTimeout(() => onClose(), 1200);
    } else {
      setStatus('error');
      setMsg(result.msg);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-sm p-8 relative">
        {/* 닫기 */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer"
        >
          <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center"></i>
        </button>

        {/* 헤더 */}
        <div className="text-center mb-7">
          <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
            <i className="ri-user-line text-emerald-600 text-xl w-6 h-6 flex items-center justify-center"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Noto Serif KR, serif' }}>
            회원 로그인
          </h2>
          <p className="text-xs text-gray-500 mt-1">회원 ID와 전화번호로 로그인하세요</p>
        </div>

        {status === 'success' ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-3">
              <i className="ri-check-line text-emerald-600 text-2xl w-7 h-7 flex items-center justify-center"></i>
            </div>
            <p className="text-base font-semibold text-gray-900">{msg}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* 아이디 */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                회원 ID <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-user-line text-base"></i>
                </span>
                <input
                  type="text"
                  value={memberId}
                  onChange={(e) => setMemberId(e.target.value)}
                  placeholder="회원 ID 입력"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                />
              </div>
            </div>

            {/* 비밀번호 (전화번호) */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-gray-700">
                비밀번호 <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                  <i className="ri-lock-line text-base"></i>
                </span>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="비밀번호 입력"
                  className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`${showPw ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                </button>
              </div>
            </div>

            {/* 에러 메시지 */}
            {status === 'error' && msg && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                  <i className="ri-error-warning-line text-sm"></i>
                </span>
                <p className="text-xs text-red-600">{msg}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full py-3.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-1"
            >
              {status === 'loading' ? (
                <>
                  <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                  로그인 중...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line w-4 h-4 flex items-center justify-center"></i>
                  로그인
                </>
              )}
            </button>

            <p className="text-xs text-gray-400 text-center">
              아직 회원이 아니신가요?{' '}
              <button
                type="button"
                onClick={() => {
                  onClose();
                  setTimeout(() => {
                    document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
                  }, 300);
                }}
                className="text-emerald-600 font-semibold underline underline-offset-2 hover:text-emerald-700 cursor-pointer"
              >
                회원가입
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
